import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Alert, Button, Collapse, Drawer, Input, Modal, Switch, Table, Upload } from "antd"
import type { TableColumnsType, UploadFile } from "antd"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faFileArrowDown,
  faFileArrowUp,
  faRotateLeft,
  faTrash,
} from "@fortawesome/free-solid-svg-icons"
import { useProjectStore } from "stores/projectStore"
import DataQButton from "shared/ui/Buttons/DataQButton.component"
import { notify } from "shared/ui/Notifications/notify"
import { downloadWorkbook } from "utils/excel/exportTableModels"
import {
  BAD_FILE,
  buildLabelSheet,
  describeImport,
  labelFileName,
  planImport,
  readLabelSheet,
  sheetRowsOf,
} from "../../export/labelSheet"
import { LabelChange, labelKey, refusedShownLabel } from "../../model/shownLabels"
import { useTabSpecStore } from "../../runtime/tabSpecStore"
import type { Catalogue } from "../../types/catalogue"
import { SPEC_DIALOG_Z_INDEX } from "../Header/SpecHeader.component"
import DraftInput from "../Inspector/DraftInput.component"
import {
  buildLabelRows,
  ItemRow,
  LabelRow,
  listLabelRows,
  NOT_IN_PROJECT,
  othersOf,
  reuseRows,
  VariableRow,
} from "./labelRows"
import ReplacePanel from "./ReplacePanel.component"

const INTRO_QUESTIONNAIRE =
  "The names the tables show. Changing one here changes no script and no filter. To change the label itself, edit the questionnaire."
const INTRO_DATA =
  "The names the tables show. Changing one here changes no script and no filter: they keep the labels of the data."
const WAITING_QUESTIONNAIRE = "Waiting for the questionnaire."
const GONE_HINT =
  "These labels name a variable or category the project no longer has. They are kept until you remove them."
const WAITING_HINT = "These labels will be checked once the data is loaded."
const RESET_ALL_QUESTIONNAIRE =
  "Every table shows the labels of the questionnaire and the data again."
const RESET_ALL_DATA = "Every table shows the labels of the data again."

/**
 * A project without a questionnaire before its catalogue is set: nothing of
 * the data is known yet, so every stored label waits for it.
 */
const NO_DATA_YET: Catalogue = { questions: [], knownPaths: null, hasRouting: false, dataOnly: true }
const NO_ROWS: LabelRow[] = []
/** The upload keeps no list of files: each one is read once and dropped. */
const NO_FILES: UploadFile[] = []

/** Every row has this height: a small field and the cell padding (tabSpec.css). */
const ROW_HEIGHT = 40
const ROW_STYLE: React.CSSProperties = { height: ROW_HEIGHT }
/** The row height for the virtual list's estimate; antd hands it on to rc-table (see TableGrid). */
const VIRTUAL_ROWS = { listItemHeight: ROW_HEIGHT }
/** Height of the list's header row, taken off the measured area; tabSpec.css sets the header to it. */
const HEADER_HEIGHT = 40
const MIN_BODY_HEIGHT = 200
/** The narrowest the list lays out: the two text columns share what the reset column leaves. */
const MIN_WIDTH = 520

const countText = (n: number): string =>
  n === 0
    ? "No label changed"
    : n === 1
      ? "1 label changed"
      : `${n.toLocaleString("en-US")} labels changed`

const isEditable = (r: LabelRow): r is VariableRow | ItemRow =>
  r.kind === "variable" || r.kind === "item"

/** Whether a row stands for a stored shown label. */
const isChanged = (r: LabelRow): boolean => (isEditable(r) ? r.shown !== null : r.kind === "gone")

/** A file's bytes, through FileReader (a Blob's own `arrayBuffer` is not there everywhere). */
const readBytes = (file: Blob): Promise<ArrayBuffer> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result as ArrayBuffer)
    reader.onerror = () => reject(reader.error ?? new Error(BAD_FILE))
    reader.readAsArrayBuffer(file)
  })

/**
 * The height the virtual list gets: the measured height of its area, less
 * the header row (as the grid of tables measures its own).
 */
const useListHeight = (): [(node: HTMLDivElement | null) => void, number] => {
  const [height, setHeight] = useState(MIN_BODY_HEIGHT)
  const observer = useRef<ResizeObserver | null>(null)
  const ref = useCallback((node: HTMLDivElement | null) => {
    observer.current?.disconnect()
    observer.current = null
    if (!node) return
    const update = (): void =>
      setHeight(Math.max(MIN_BODY_HEIGHT, node.clientHeight - HEADER_HEIGHT))
    update()
    if (typeof ResizeObserver === "undefined") return
    observer.current = new ResizeObserver(update)
    observer.current.observe(node)
  }, [])
  useEffect(() => () => observer.current?.disconnect(), [])
  return [ref, height]
}

const onRow = (): React.HTMLAttributes<HTMLElement> => ({ style: ROW_STYLE })
const rowClassName = (r: LabelRow): string => `ts-labels__row ts-labels__row--${r.kind}`
/** A cell renders again only for a new row object (`reuseRows` keeps the others). */
const onNewRow = (a: LabelRow, b: LabelRow): boolean => a !== b
/** The heading of the rows under it spans the three columns. */
const spanHeading = (r: LabelRow): React.TdHTMLAttributes<HTMLElement> =>
  r.kind === "heading" ? { colSpan: 3 } : {}
const underHeading = (r: LabelRow): React.TdHTMLAttributes<HTMLElement> =>
  r.kind === "heading" ? { colSpan: 0 } : {}

/** The Label cell: the raw label (whole in its title), and what the row is. */
const labelCell = (r: LabelRow, onRemoveAll: () => void): React.ReactNode => {
  switch (r.kind) {
    case "variable":
      return (
        <span className="ts-labels__variable" title={r.raw}>
          <strong className="ts-labels__name">{r.name}</strong>
          {r.within && <span className="ts-labels__within">{r.within}</span>}
          <span className="ts-chip ts-labels__phrase">{r.phrase}</span>
        </span>
      )
    case "item":
      return (
        <span className="ts-labels__item" title={r.raw}>
          <span className="ts-labels__raw">{r.raw}</span>
          <span className="ts-labels__word">{r.word}</span>
        </span>
      )
    case "gone":
      return (
        <span className="ts-labels__item" title={r.raw}>
          <span className="ts-labels__raw">{r.raw}</span>
        </span>
      )
    case "heading": {
      const gone = r.text === NOT_IN_PROJECT
      const hint = gone ? GONE_HINT : WAITING_HINT
      return (
        <div className="ts-labels__heading">
          <strong>{r.text}</strong>
          <span className="ts-labels__hint" title={hint}>
            {hint}
          </span>
          {gone && (
            <Button size="small" onClick={onRemoveAll}>
              Remove all
            </Button>
          )}
        </div>
      )
    }
  }
}

/** What the drawer says after an upload: the counts and refusals, or why the file was not read. */
interface UploadResult {
  type: "info" | "error"
  lines: string[]
}

/**
 * The drawer's contents, mounted while it is open: the rows are built from
 * the catalogue and the shown labels only then.
 */
const LabelsBody: React.FC = () => {
  const labels = useTabSpecStore((s) => s.document.labels)
  const catalogue = useTabSpecStore((s) => s.catalogue)
  const dataOnly = useTabSpecStore((s) => s.dataOnly)
  const specName = useTabSpecStore((s) => s.name)
  const setShownLabels = useTabSpecStore((s) => s.setShownLabels)
  const projectName = useProjectStore((s) => s.project.Name)
  const source = catalogue ?? (dataOnly ? NO_DATA_YET : null)

  const [search, setSearch] = useState("")
  const [changedOnly, setChangedOnly] = useState(false)
  const [confirmReset, setConfirmReset] = useState(false)
  const [result, setResult] = useState<UploadResult | null>(null)
  const [listRef, listHeight] = useListHeight()
  const anchorRef = useRef<HTMLSpanElement>(null)
  // The dialogs render inside the spec, so they get the --qc-* tokens.
  const dialogContainer = useCallback(
    (): HTMLElement => anchorRef.current?.closest<HTMLElement>(".tabspec-root") ?? document.body,
    []
  )

  // The rows as last built, by key: a row keeps its object while it shows
  // the same, so an edit of one label builds one row object.
  const built = useRef(new Map<string, LabelRow>())
  const rows = useMemo((): LabelRow[] => {
    if (!source) return NO_ROWS
    const next = reuseRows(built.current, buildLabelRows(source, labels))
    built.current = next.index
    return next.rows
  }, [source, labels])
  const listed = useMemo(
    () => listLabelRows(rows, search, changedOnly),
    [rows, search, changedOnly]
  )
  const changed = useMemo(() => rows.filter(isChanged).length, [rows])

  // Read when a field is left or a button pressed, so the cells (which
  // render again only for a new row object) never hold an older list.
  const rowsRef = useRef(rows)
  useEffect(() => {
    rowsRef.current = rows
  }, [rows])
  // An upload made before the data is loaded keeps the rows that may name a
  // variable of the data, to wait for it.
  const dataLoaded = source !== null && source.knownPaths !== null
  const dataLoadedRef = useRef(dataLoaded)
  useEffect(() => {
    dataLoadedRef.current = dataLoaded
  }, [dataLoaded])

  const clear = useCallback(
    (targets: LabelRow[]): void => {
      const changes: LabelChange[] = targets.flatMap((r) =>
        r.kind === "heading" ? [] : [{ target: r.target, shown: null }]
      )
      if (changes.length > 0) setShownLabels(changes)
    },
    [setShownLabels]
  )
  const removeGone = useCallback(
    (): void => clear(rowsRef.current.filter((r) => r.kind === "gone")),
    [clear]
  )

  /**
   * Writes what was typed in the Shown as field of `row`: empty, or the raw
   * label itself, resets it; a refused name stays in the field with why.
   */
  const commit = useCallback(
    (row: VariableRow | ItemRow, text: string): string | null => {
      const trimmed = text.trim()
      if (!trimmed || trimmed === row.raw) {
        if (row.shown !== null) setShownLabels([{ target: row.target, shown: null }])
        return null
      }
      if (trimmed === row.shown) return null
      let reason: string | null
      if (row.kind === "item") {
        const owner = built.current.get(labelKey({ variable: row.variable }))
        reason = refusedShownLabel(trimmed, othersOf(rowsRef.current, row), {
          word: row.word,
          owner: owner && owner.kind === "variable" ? (owner.shown ?? owner.raw) : row.variable,
        })
      } else reason = refusedShownLabel(trimmed, [], null)
      if (reason) return reason
      setShownLabels([{ target: row.target, shown: trimmed }])
      return null
    },
    [setShownLabels]
  )

  const columns = useMemo(
    (): TableColumnsType<LabelRow> => [
      {
        key: "label",
        title: "Label",
        onCell: spanHeading,
        shouldCellUpdate: onNewRow,
        render: (_: unknown, r: LabelRow) => labelCell(r, removeGone),
      },
      {
        key: "shown",
        title: "Shown as",
        onCell: underHeading,
        shouldCellUpdate: onNewRow,
        render: (_: unknown, r: LabelRow) =>
          isEditable(r) ? (
            <DraftInput
              className="ts-labels__field"
              value={r.shown ?? ""}
              placeholder={r.raw}
              ariaLabel={`Shown label of ${r.raw}`}
              errorDisplay="tooltip"
              onCommit={(text) => commit(r, text)}
            />
          ) : r.kind === "gone" ? (
            <span className="ts-labels__stored" title={r.shown}>
              {r.shown}
            </span>
          ) : null,
      },
      {
        key: "reset",
        width: 44,
        onCell: underHeading,
        shouldCellUpdate: onNewRow,
        render: (_: unknown, r: LabelRow) =>
          r.kind === "gone" ? (
            <Button
              size="small"
              type="text"
              danger
              icon={<FontAwesomeIcon icon={faTrash} />}
              aria-label={`Remove ${r.raw}`}
              title="Remove"
              onClick={() => clear([r])}
            />
          ) : isEditable(r) && r.shown !== null ? (
            <Button
              size="small"
              type="text"
              icon={<FontAwesomeIcon icon={faRotateLeft} />}
              aria-label={`Reset ${r.raw}`}
              title="Reset"
              onClick={() => clear([r])}
            />
          ) : null,
      },
    ],
    [clear, commit, removeGone]
  )
  const scroll = useMemo(() => ({ x: MIN_WIDTH, y: listHeight }), [listHeight])

  const download = async (): Promise<void> => {
    try {
      await downloadWorkbook(
        buildLabelSheet(sheetRowsOf(rowsRef.current)),
        labelFileName(projectName, specName)
      )
    } catch (e) {
      notify.error(
        "Labels",
        e instanceof Error && e.message ? e.message : "The label list could not be written."
      )
    }
  }

  const upload = async (file: Blob): Promise<void> => {
    try {
      const read = await readLabelSheet(await readBytes(file))
      if ("error" in read) {
        setResult({ type: "error", lines: [read.error] })
        return
      }
      const plan = planImport(read.rows, rowsRef.current, dataLoadedRef.current)
      if (plan.changes.length > 0) setShownLabels(plan.changes)
      setResult({ type: "info", lines: describeImport(plan, read.unknown) })
    } catch {
      setResult({ type: "error", lines: [BAD_FILE] })
    }
  }

  const resetAll = (): void => {
    clear(rowsRef.current.filter(isChanged))
    setConfirmReset(false)
  }

  const intro = (
    <p className="ts-labels__intro">{dataOnly ? INTRO_DATA : INTRO_QUESTIONNAIRE}</p>
  )
  if (!source)
    return (
      <div className="ts-labels">
        {intro}
        <p className="ts-hint">{WAITING_QUESTIONNAIRE}</p>
      </div>
    )

  return (
    <div className="ts-labels">
      <span ref={anchorRef} hidden />
      {intro}
      <div className="ts-labels__toolbar" role="toolbar" aria-label="Find labels">
        <Input.Search
          allowClear
          size="small"
          className="ts-labels__search"
          placeholder="Search labels"
          aria-label="Search labels"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <span className="ts-labels__switch">
          <Switch
            size="small"
            aria-label="Changed only"
            checked={changedOnly}
            onChange={setChangedOnly}
          />
          <span>Changed only</span>
        </span>
        <span className="ts-labels__count" aria-live="polite">
          {countText(changed)}
        </span>
        <span className="ts-labels__actions">
          <Button
            size="small"
            icon={<FontAwesomeIcon icon={faFileArrowDown} />}
            onClick={() => void download()}
          >
            Download list
          </Button>
          <Upload
            accept=".xlsx"
            showUploadList={false}
            fileList={NO_FILES}
            beforeUpload={(file) => {
              void upload(file)
              // Read here; nothing is sent anywhere.
              return false
            }}
          >
            <Button size="small" icon={<FontAwesomeIcon icon={faFileArrowUp} />}>
              Upload list
            </Button>
          </Upload>
          <Button size="small" disabled={changed === 0} onClick={() => setConfirmReset(true)}>
            Reset all
          </Button>
        </span>
      </div>
      <Collapse
        ghost
        size="small"
        className="ts-labels__replace"
        items={[
          {
            key: "replace",
            label: "Find and replace",
            children: (
              <ReplacePanel
                listed={listed}
                rows={rows}
                onReplace={setShownLabels}
                getContainer={dialogContainer}
              />
            ),
          },
        ]}
      />
      {result && (
        <Alert
          className="ts-labels__result"
          type={result.type}
          showIcon
          closable
          onClose={() => setResult(null)}
          message={result.lines[0]}
          description={
            result.lines.length > 1 ? (
              <ul className="ts-labels__result-lines">
                {result.lines.slice(1).map((line, i) => (
                  <li key={i}>{line}</li>
                ))}
              </ul>
            ) : undefined
          }
        />
      )}
      <div ref={listRef} className="ts-labels__list">
        <Table<LabelRow>
          size="small"
          virtual
          {...VIRTUAL_ROWS}
          pagination={false}
          rowKey="key"
          columns={columns}
          dataSource={listed}
          scroll={scroll}
          onRow={onRow}
          rowClassName={rowClassName}
          locale={{ emptyText: "No label matches." }}
        />
      </div>
      {/* A confirmation in the platform's words and buttons: the way back
          first, the destructive action last. */}
      <Modal
        open={confirmReset}
        title="Reset all labels"
        onCancel={() => setConfirmReset(false)}
        getContainer={dialogContainer}
        zIndex={SPEC_DIALOG_Z_INDEX}
        destroyOnHidden
        footer={[
          <DataQButton key="cancel" type="secondary" onClick={() => setConfirmReset(false)}>
            Cancel
          </DataQButton>,
          <DataQButton key="reset" type="danger" onClick={resetAll}>
            Reset all
          </DataQButton>,
        ]}
      >
        <p>{dataOnly ? RESET_ALL_DATA : RESET_ALL_QUESTIONNAIRE}</p>
      </Modal>
    </div>
  )
}

export interface LabelsDrawerProps {
  open: boolean
  onClose: () => void
  /** Where the drawer renders: inside the spec, so it gets the --qc-* tokens. */
  getContainer?: () => HTMLElement
}

/**
 * The names the tables show, one row per variable and per category, grid
 * row, cell or loop iteration, in the catalogue's order: the questionnaire's
 * questions first, then the variables of the data. A name typed in "Shown
 * as" is written when the field is left or Enter is pressed; the raw label
 * stays the identity of every script and filter. Search, "Changed only",
 * find and replace, a download and upload of the list as an Excel sheet, and
 * a reset of one or all. Labels that name nothing of the project are listed
 * last, to be removed. Everything is built while the drawer is open only.
 */
const LabelsDrawer: React.FC<LabelsDrawerProps> = ({ open, onClose, getContainer }) => (
  <Drawer
    open={open}
    onClose={onClose}
    getContainer={getContainer}
    rootClassName="ts-drawer"
    classNames={{ body: "ts-labels__drawer-body" }}
    placement="right"
    width="min(880px, 94vw)"
    title="Labels"
    destroyOnHidden
  >
    <LabelsBody />
  </Drawer>
)

export default LabelsDrawer
